// src/components/AdminOverlay.js
import React, { useEffect, useMemo, useState } from 'react';
import LogsOverlay from './LogsOverlay';
import SupportTicketsOverlay from './SupportTicketsOverlay';
import AISettingsOverlay from './AISettingsOverlay';

/*
Tiers Breakdown:
  Admin:
  - full access to everything (me)
  - No Ads

  All_Access (Direct Paypal Subscription):
  - Access to all Products
  - Access to My Projects and My Storefront
  - Access to advanced AI features
  - No Ads

  Patreon_Access:
  - Access to all Products
  - Access to My Projects and My Storefront
  - No Ads (until All_Access is introduced, than switch to side ads)

  support:
  - Access to all Products
  - Side Ads

  free:
  - Limited Product Access to: 3D Sign, Keychain, Coaster.
  - Side Ads and Download-Wall ads
*/

export default function AdminOverlay({
  email,
  onClose,
  onClearCache, // (kept for compatibility; not used now that we do per-user clears here)
  onToggleMaintenance,
  maintenance,
}) {
  const [newEmail, setNewEmail] = useState('');
  const [newTier, setNewTier] = useState('free');
  const [newCredits, setNewCredits] = useState('');

  // user list & selection
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  // login image mode can be 'default' | 'seasonal'
  const [loginImageMode, setLoginImageMode] = useState('seasonal');
  const [isTogglingLoginMode, setIsTogglingLoginMode] = useState(false);

  const [showLogs, setShowLogs] = useState(false);
  const [showTickets, setShowTickets] = useState(false);

  // ✅ DAU state
  const [dau, setDau] = useState(0);

  // ✅ User DB overlay state
  const [showUserDb, setShowUserDb] = useState(false);
  const [userDbRows, setUserDbRows] = useState([]);
  const [userDbLoading, setUserDbLoading] = useState(false);
  const [userDbError, setUserDbError] = useState('');

  // ✅ Responsive flag for the Users DB overlay
  const [userDbIsMobile, setUserDbIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 720px)').matches : false
  );

  // ✅ Ship a Design overlay state (inline overlay in THIS script)
  const [showShip, setShowShip] = useState(false);
  const [shipIsMobile, setShipIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 720px)').matches : false
  );

  const [shipProjects, setShipProjects] = useState([]); // array of { projectSlug, label? }
  const [shipUsers, setShipUsers] = useState([]); // array of { email, tier, credits }
  const [shipSelectedProject, setShipSelectedProject] = useState('');
  const [shipSelectedUserEmail, setShipSelectedUserEmail] = useState('');
  const [shipDeliveryMode, setShipDeliveryMode] = useState('free'); // 'free' | 'credits'
  const [shipNotes, setShipNotes] = useState('');
  const [shipLoading, setShipLoading] = useState(false);
  const [shipError, setShipError] = useState('');

  // ✅ Admin verification state (used to gate EVERY button click)
  const [isVerifyingAdmin, setIsVerifyingAdmin] = useState(false);
  const [adminVerifyError, setAdminVerifyError] = useState('');

  // ✅ AI Settings overlay
  const [showAISettings, setShowAISettings] = useState(false);

  const adminUsername = useMemo(() => (email || '').split('@')[0].trim(), [email]);

  // Centralized admin-gate endpoint.
  const verifyAdmin = async () => {
    const username = adminUsername;
    const headers = {};
    if (username) headers.user = username;

    const res = await fetch('/verify-admin/', { method: 'GET', headers });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`verify-admin failed: ${res.status} ${t}`);
    }

    const json = await res.json().catch(() => ({}));
    const ok =
      json?.ok === true ||
      json?.isAdmin === true ||
      json?.admin === true ||
      String(json?.tier || '').toLowerCase() === 'admin';

    if (!ok) {
      throw new Error('Not authorized (admin required).');
    }

    return true;
  };

  // Wrap any onClick handler with admin verification.
  const withAdminGate = (fn, { silent = false } = {}) => {
    return async (...args) => {
      if (isVerifyingAdmin) return;
      setAdminVerifyError('');

      try {
        setIsVerifyingAdmin(true);
        await verifyAdmin();
        await fn(...args);
      } catch (err) {
        console.error('Admin verification blocked action:', err);
        const msg = err?.message || 'Admin verification failed.';
        setAdminVerifyError(msg);
        if (!silent) alert('Admin access required.');
      } finally {
        setIsVerifyingAdmin(false);
      }
    };
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = (e) => setUserDbIsMobile(!!e.matches);

    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    setUserDbIsMobile(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Ship overlay mobile media query
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = (e) => setShipIsMobile(!!e.matches);

    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    setShipIsMobile(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const resetUserFields = () => {
    setNewTier('free');
    setNewCredits('');
  };

  useEffect(() => {
    fetch('/list-users/')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.users)) {
          const rawUsers = data.users;

          // Build a map of baseName -> { hasBaseDir, hasCustomerDirs }
          const baseMap = new Map();

          for (const u of rawUsers) {
            const [base, rest] = String(u).split(':', 2);
            const info = baseMap.get(base) || { hasBaseDir: false, hasCustomerDirs: false };

            if (rest === undefined || rest === '') {
              info.hasBaseDir = true; // plain "erez.habousha"
            } else {
              info.hasCustomerDirs = true; // "erez.habousha:anything"
            }

            baseMap.set(base, info);
          }

          const displayUsers = [];
          for (const [base, info] of baseMap.entries()) {
            if (info.hasBaseDir) displayUsers.push(base);
            if (info.hasCustomerDirs) displayUsers.push(`${base}:customer`);
          }

          const fullList = [...displayUsers, 'All Users'];
          setUserList(fullList);

          if (displayUsers.length > 0) setSelectedUser(displayUsers[0]);
          else setSelectedUser('All Users');
        } else {
          setUserList(['All Users']);
          setSelectedUser('All Users');
        }
      })
      .catch((err) => {
        console.error('Failed to fetch user list:', err);
        setUserList(['All Users']);
        setSelectedUser('All Users');
      });

    // Login image mode
    fetch('/login-mode/')
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Login mode fetch failed: ${res.status} ${t}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data && (data.login_mode === 'default' || data.login_mode === 'seasonal')) {
          setLoginImageMode(data.login_mode);
        }
      })
      .catch((err) => console.error('Failed to fetch login image mode:', err));

    // ✅ Fetch DAU count
    fetch('/dau/')
      .then((res) => res.json())
      .then((data) => {
        if (typeof data.dau === 'number') {
          setDau(data.dau);
        }
      })
      .catch((err) => console.error('Failed to fetch DAU:', err));
  }, []);

  const handleAddUser = withAdminGate(async () => {
    try {
      const res = await fetch('/add-user/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(adminUsername ? { user: adminUsername } : {}) },
        body: JSON.stringify({
          email: newEmail,
          subscription_tier: newTier,
          credits: parseInt(newCredits, 10),
        }),
      });
      const json = await res.json();
      alert(json.message || 'User added');
    } catch (err) {
      console.error('Add user failed:', err);
      alert('Failed to add user');
    }
  });

  const handleRemoveUser = withAdminGate(async () => {
    try {
      const res = await fetch('/remove-user/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(adminUsername ? { user: adminUsername } : {}) },
        body: JSON.stringify({ email: newEmail }),
      });
      const json = await res.json();
      alert(json.message || 'User removed');
    } catch (err) {
      console.error('Remove user failed:', err);
      alert('Failed to remove user');
    }
  });

  const handleUpdateUserFixed = withAdminGate(async () => {
    const emailVal = newEmail.trim();
    if (!emailVal) {
      alert('Please enter an email before updating a user.');
      return;
    }

    const creditsValRaw = String(newCredits || '').trim();
    const creditsParsed = creditsValRaw === '' ? null : parseInt(creditsValRaw, 10);

    try {
      const res = await fetch('/update-user/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(adminUsername ? { user: adminUsername } : {}) },
        body: JSON.stringify({
          email: emailVal,
          subscription_tier: newTier,
          credits: creditsParsed,
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Update user failed: ${res.status} ${t}`);
      }

      const json = await res.json().catch(() => ({}));
      alert(json.message || 'User updated');
    } catch (err) {
      console.error('Update user failed:', err);
      alert('Failed to update user');
    }
  });

  const handleLoadUser = withAdminGate(async () => {
    const emailVal = newEmail.trim();
    if (!emailVal) return;

    try {
      let res = await fetch(`/get-user/${encodeURIComponent(emailVal)}`, {
        headers: { ...(adminUsername ? { user: adminUsername } : {}) },
      });
      if (!res.ok) {
        res = await fetch(`/get-user/?email=${encodeURIComponent(emailVal)}`, {
          headers: { ...(adminUsername ? { user: adminUsername } : {}) },
        });
      }
      if (!res.ok) {
        console.warn('Load User: user not found or endpoint not available');
        resetUserFields();
        alert('User not found. Fields have been reset.');
        return;
      }
      const data = await res.json();

      const loadedTier = data.subscription_tier ?? data.tier;
      const loadedCredits = data.credits;

      if (loadedTier !== undefined && loadedTier !== null) setNewTier(String(loadedTier));
      else setNewTier('free');

      if (loadedCredits !== undefined && loadedCredits !== null) setNewCredits(String(loadedCredits));
      else setNewCredits('');

      if (
        (loadedTier === undefined || loadedTier === null) &&
        (loadedCredits === undefined || loadedCredits === null)
      ) {
        console.warn('Load User: response lacked tier/credits fields');
        resetUserFields();
        alert('User not found. Fields have been reset.');
      }
    } catch (err) {
      console.error('Load user failed:', err);
      resetUserFields();
      alert('Error loading user. Fields have been reset.');
    }
  });

  const handleClearCache = withAdminGate(async () => {
    if (!selectedUser) {
      alert('Please select a user or "All Users" first.');
      return;
    }

    if (selectedUser === 'All Users') {
      try {
        const response = await fetch('/clear-cache/', {
          method: 'POST',
          headers: { ...(adminUsername ? { user: adminUsername } : {}) },
        });
        if (!response.ok) {
          const t = await response.text().catch(() => '');
          throw new Error(`Clear cache failed: ${response.status} ${t}`);
        }
        const data = await response.json();
        alert(`Cache cleared. ${data.deleted} files deleted.`);
      } catch (err) {
        alert('Failed to clear cache');
        console.error(err);
      }
      return;
    }

    if (selectedUser.endsWith(':customer')) {
      const baseUsername = selectedUser.slice(0, -':customer'.length);
      try {
        const res = await fetch(`/clear-customer-cache/${encodeURIComponent(baseUsername)}`, {
          method: 'POST',
          headers: { ...(adminUsername ? { user: adminUsername } : {}) },
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Clear customer cache failed: ${res.status} ${t}`);
        }
        const json = await res.json();
        alert(json.message || `Customer cache cleared for '${baseUsername}'.`);
      } catch (err) {
        console.error('Clear customer cache failed:', err);
        alert('Failed to clear customer cache');
      }
      return;
    }

    try {
      const res = await fetch(`/clear-user-cache/${encodeURIComponent(selectedUser)}`, {
        method: 'POST',
        headers: { ...(adminUsername ? { user: adminUsername } : {}) },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Clear user cache failed: ${res.status} ${t}`);
      }
      const json = await res.json();
      alert(json.message || `User cache cleared for '${selectedUser}'.`);
    } catch (err) {
      console.error('Clear user cache failed:', err);
      alert('Failed to clear user cache');
    }
  });

  const toggleLoginImageMode = withAdminGate(async () => {
    try {
      setIsTogglingLoginMode(true);

      const headers = {};
      if (adminUsername) headers.user = adminUsername;

      const res = await fetch('/toggle-login-mode/', {
        method: 'POST',
        headers,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('Toggle failed:', text);
        return;
      }
      const json = await res.json();
      if (!json || !json.login_mode) {
        console.error('Unexpected toggle response:', json);
        return;
      }
      setLoginImageMode(json.login_mode);
    } catch (err) {
      console.error('Toggle login image mode failed:', err);
    } finally {
      setIsTogglingLoginMode(false);
    }
  });

  const handleLoadUserDb = withAdminGate(async () => {
    setShowUserDb(true);
    setUserDbLoading(true);
    setUserDbError('');
    setUserDbRows([]);

    try {
      const res = await fetch('/list-db-users/', {
        headers: { ...(adminUsername ? { user: adminUsername } : {}) },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`list-db-users failed: ${res.status} ${t}`);
      }
      const data = await res.json();

      const raw = Array.isArray(data.users) ? data.users : Array.isArray(data) ? data : [];

      const normalized = raw.map((row, idx) => {
        const email = row.email || row.Email || `user-${idx}`;
        const tier = row.subscription_tier || row.tier || row.Subscription_tier || 'unknown';
        let credits = row.credits;
        if (credits === undefined || credits === null) {
          credits = row.Credits !== undefined ? row.Credits : 0;
        }
        return {
          email,
          tier,
          credits: Number.isFinite(Number(credits)) ? Number(credits) : 0,
        };
      });

      setUserDbRows(normalized);
    } catch (err) {
      console.error('Failed to load users.db:', err);
      setUserDbError('Failed to load user database. Check console / backend logs.');
    } finally {
      setUserDbLoading(false);
    }
  });

  // -------- Ship a Design helpers --------

  const loadShipProjects = async () => {
    const candidates = ['/ship-projects/', '/list-downloaded-projects/', '/admin-downloaded-projects/'];

    let lastErr = '';
    for (const url of candidates) {
      try {
        const headers = {};
        if (adminUsername) headers.user = adminUsername;

        const res = await fetch(url, { headers });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          lastErr = `${url}: ${res.status} ${t}`;
          continue;
        }
        const data = await res.json().catch(() => null);

        const raw =
          (data && Array.isArray(data.projects) && data.projects) ||
          (Array.isArray(data) ? data : []);

        const normalized = raw
          .map((p) => {
            if (typeof p === 'string') return { projectSlug: p, label: p };
            if (p && typeof p === 'object') {
              const slug = p.projectSlug || p.slug || p.project || '';
              const label = p.label || slug || '';
              if (!slug) return null;
              return { projectSlug: String(slug), label: String(label) };
            }
            return null;
          })
          .filter(Boolean);

        return normalized;
      } catch (e) {
        lastErr = `${url}: ${String(e)}`;
      }
    }

    throw new Error(lastErr || 'Failed to load projects');
  };

  const loadShipUsers = async () => {
    const res = await fetch('/list-db-users/', {
      headers: { ...(adminUsername ? { user: adminUsername } : {}) },
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`list-db-users failed: ${res.status} ${t}`);
    }
    const data = await res.json();

    const raw = Array.isArray(data.users) ? data.users : Array.isArray(data) ? data : [];

    const normalized = raw
      .map((row) => {
        const email = row.email || row.Email || '';
        const tier = row.subscription_tier || row.tier || row.Subscription_tier || 'unknown';
        let credits = row.credits;
        if (credits === undefined || credits === null) credits = row.Credits !== undefined ? row.Credits : 0;
        if (!email) return null;
        return {
          email: String(email),
          tier: String(tier),
          credits: Number.isFinite(Number(credits)) ? Number(credits) : 0,
        };
      })
      .filter(Boolean);

    normalized.sort((a, b) => a.email.localeCompare(b.email));
    return normalized;
  };

  const openShipOverlay = withAdminGate(async () => {
    setShowShip(true);
    setShipError('');
    setShipLoading(true);
    setShipProjects([]);
    setShipUsers([]);
    setShipSelectedProject('');
    setShipSelectedUserEmail('');
    setShipDeliveryMode('free');
    setShipNotes('');

    try {
      const [projects, users] = await Promise.all([loadShipProjects(), loadShipUsers()]);
      setShipProjects(projects);
      setShipUsers(users);
      if (projects.length > 0) setShipSelectedProject(projects[0].projectSlug);
      if (users.length > 0) setShipSelectedUserEmail(users[0].email);
    } catch (err) {
      console.error('Ship overlay load failed:', err);
      setShipError('Failed to load projects/users. Check console / backend logs.');
    } finally {
      setShipLoading(false);
    }
  });

  const handleShipToUser = withAdminGate(async () => {
    if (shipLoading) return;

    const projectSlug = String(shipSelectedProject || '').trim();
    const customerEmail = String(shipSelectedUserEmail || '').trim();

    if (!projectSlug) {
      alert('Please choose a project first.');
      return;
    }
    if (!customerEmail) {
      alert('Please choose a user first.');
      return;
    }

    try {
      setShipLoading(true);
      setShipError('');

      const ownerUsername = adminUsername || 'admin';
      const customerName = customerEmail.split('@')[0] || customerEmail;

      const res = await fetch('/ship-design/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(ownerUsername ? { user: ownerUsername } : {}),
        },
        body: JSON.stringify({
          ownerUsername,
          projectSlug,
          customerEmail,
          customerName,
          deliveryMode: shipDeliveryMode,
          notes: shipNotes || '',
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Ship failed: ${res.status} ${t}`);
      }

      const json = await res.json().catch(() => ({}));
      alert(json.message || 'Design shipped successfully.');
    } catch (err) {
      console.error('ShipToUser failed:', err);
      setShipError('Ship failed. Check console / backend logs.');
      alert('Ship failed.');
    } finally {
      setShipLoading(false);
    }
  });

  const openLogs = withAdminGate(async () => setShowLogs(true), { silent: true });
  const openTickets = withAdminGate(async () => setShowTickets(true), { silent: true });
  const closeOverlay = withAdminGate(async () => onClose?.(), { silent: true });

  const toggleMaintenance = withAdminGate(async () => {
    await onToggleMaintenance?.();
  });

  const closeUserDbOverlay = withAdminGate(async () => setShowUserDb(false), { silent: true });
  const closeShipOverlay = withAdminGate(async () => setShowShip(false), { silent: true });

  const refreshShipOverlay = withAdminGate(async () => {
    setShipError('');
    setShipLoading(true);
    try {
      const [projects, users] = await Promise.all([loadShipProjects(), loadShipUsers()]);
      setShipProjects(projects);
      setShipUsers(users);
      if (projects.length > 0) setShipSelectedProject(projects[0].projectSlug);
      if (users.length > 0) setShipSelectedUserEmail(users[0].email);
    } catch (err) {
      console.error('Ship overlay refresh failed:', err);
      setShipError('Failed to refresh projects/users. Check console / backend logs.');
    } finally {
      setShipLoading(false);
    }
  });

  const openAISettings = withAdminGate(async () => setShowAISettings(true), { silent: true });
  const closeAISettings = withAdminGate(async () => setShowAISettings(false), { silent: true });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '10px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          alignItems: 'center',
          boxShadow: '0 0 15px rgba(0,0,0,0.3)',
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ fontSize: '1.4em', fontWeight: 'bold', textAlign: 'center' }}>
          Admin Dashboard
          <div style={{ fontSize: '0.9em', marginTop: '4px', color: '#40E0D0' }}>DAU: {dau}</div>
        </h2>

        {adminVerifyError && (
          <div
            style={{
              width: '100%',
              color: '#b91c1c',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          >
            {adminVerifyError}
          </div>
        )}

        <div
          onClick={() => {
            if (isTogglingLoginMode || isVerifyingAdmin) return;
            toggleLoginImageMode();
          }}
          title="Toggle between default and seasonal login images"
          style={{
            position: 'relative',
            width: '170px',
            height: '32px',
            borderRadius: '999px',
            border: '1px solid #d1d5db',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            cursor: isTogglingLoginMode || isVerifyingAdmin ? 'default' : 'pointer',
            opacity: isTogglingLoginMode || isVerifyingAdmin ? 0.6 : 1,
            transition: 'opacity .15s',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              width: '50%',
              borderRadius: '999px',
              backgroundColor: '#fbbf24',
              left: loginImageMode === 'default' ? 2 : 'calc(50% - 2px)',
              transition: 'left 0.2s ease',
              zIndex: 0,
            }}
          />
          <span style={{ zIndex: 1, fontWeight: loginImageMode === 'default' ? 'bold' : 'normal' }}>
            Default
          </span>
          <span style={{ zIndex: 1, fontWeight: loginImageMode === 'seasonal' ? 'bold' : 'normal' }}>
            Seasonal
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            {userList.map((user) => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </select>
          <button onClick={handleClearCache} disabled={isVerifyingAdmin} style={buttonStyle('#ef4444')}>
            Clear Cache
          </button>
        </div>

        <button
          onClick={toggleMaintenance}
          disabled={isVerifyingAdmin}
          style={buttonStyle(maintenance ? '#10b981' : '#9ca3af')}
        >
          {maintenance ? 'Maintenance ON' : 'Maintenance OFF'}
        </button>

        <button onClick={openLogs} disabled={isVerifyingAdmin} style={buttonStyle('#22c55e')}>
          User Logs
        </button>

        <button onClick={openTickets} disabled={isVerifyingAdmin} style={buttonStyle('#3b82f6')}>
          Support Tickets
        </button>

        <button onClick={openShipOverlay} disabled={isVerifyingAdmin} style={buttonStyle('#f59e0b')}>
          Ship a Design
        </button>

        <button onClick={openAISettings} disabled={isVerifyingAdmin} style={buttonStyle('#8b5cf6')}>
          AI Settings
        </button>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <input
            type="text"
            placeholder="Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            style={inputStyle}
          />

          <select value={newTier} onChange={(e) => setNewTier(e.target.value)} style={inputStyle}>
            <option value="free">free</option>
            <option value="support">support</option>
            <option value="all_access">all_access</option>
            <option value="patreon_access">patreon_access</option>
            <option value="admin">admin</option>
          </select>

          <input
            type="number"
            placeholder="Credits"
            value={newCredits}
            onChange={(e) => setNewCredits(e.target.value)}
            style={inputStyle}
          />

          <button onClick={handleAddUser} disabled={isVerifyingAdmin} style={buttonStyle('#2563eb')}>
            Add New User
          </button>
          <button onClick={handleRemoveUser} disabled={isVerifyingAdmin} style={buttonStyle('#b91c1c')}>
            Remove User
          </button>
          <button onClick={handleLoadUser} disabled={isVerifyingAdmin} style={buttonStyle('#0ea5e9')}>
            Load User
          </button>
          <button
            onClick={handleUpdateUserFixed}
            disabled={isVerifyingAdmin}
            style={buttonStyle('#111827')}
            title="Updates tier and credits for the email above"
          >
            Update User
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={handleLoadUserDb} disabled={isVerifyingAdmin} style={buttonStyle('#10b981')}>
            Load User Database
          </button>
        </div>

        <button onClick={closeOverlay} disabled={isVerifyingAdmin} style={buttonStyle('#6b7280')}>
          Close
        </button>
      </div>

      {showLogs && <LogsOverlay onClose={() => setShowLogs(false)} />}
      {showTickets && <SupportTicketsOverlay onClose={() => setShowTickets(false)} />}
      {showAISettings && <AISettingsOverlay adminUsername={adminUsername} onClose={closeAISettings} />}

      {showUserDb && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: userDbIsMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: userDbIsMobile ? 0 : 12,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: userDbIsMobile ? '14px 14px 0 0' : '10px',
              padding: userDbIsMobile ? 12 : 16,
              width: userDbIsMobile ? '100%' : '95%',
              maxWidth: userDbIsMobile ? '100%' : '900px',
              maxHeight: userDbIsMobile ? '92vh' : '80vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 0 20px rgba(0,0,0,0.4)',
              boxSizing: 'border-box',
              paddingBottom: userDbIsMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
                gap: 8,
              }}
            >
              <h3 style={{ margin: 0, fontSize: userDbIsMobile ? '1rem' : '1.1rem' }}>Users Database</h3>
              <button
                onClick={closeUserDbOverlay}
                disabled={isVerifyingAdmin}
                style={{
                  ...buttonStyle('#6b7280'),
                  padding: userDbIsMobile ? '6px 10px' : '4px 10px',
                  fontSize: userDbIsMobile ? '0.85rem' : '0.8rem',
                  touchAction: 'manipulation',
                }}
              >
                Close
              </button>
            </div>

            {userDbLoading && <div style={{ padding: '8px 0' }}>Loading users.db…</div>}
            {userDbError && <div style={{ color: 'red', marginBottom: 8 }}>{userDbError}</div>}

            {!userDbLoading && !userDbError && userDbRows.length === 0 && (
              <div style={{ padding: '8px 0' }}>No users found in users.db.</div>
            )}

            {!userDbLoading && !userDbError && userDbRows.length > 0 && (
              <>
                {!userDbIsMobile ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Tier</th>
                        <th style={thStyle}>Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userDbRows.map((u) => (
                        <tr key={u.email}>
                          <td style={tdStyle}>{u.email}</td>
                          <td style={tdStyle}>{u.tier}</td>
                          <td style={tdStyle}>{u.credits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {userDbRows.map((u) => (
                      <div
                        key={u.email}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 10,
                          padding: 12,
                          background: '#fff',
                        }}
                      >
                        <div style={{ fontWeight: 800, wordBreak: 'break-word' }}>{u.email}</div>
                        <div style={{ marginTop: 6, fontSize: 13, color: '#374151' }}>
                          <span style={{ color: '#6b7280', fontWeight: 700 }}>Tier:</span>{' '}
                          <span style={{ textTransform: 'capitalize' }}>{u.tier}</span>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 13, color: '#374151' }}>
                          <span style={{ color: '#6b7280', fontWeight: 700 }}>Credits:</span> {u.credits}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showShip && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: shipIsMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: shipIsMobile ? 0 : 12,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: shipIsMobile ? '14px 14px 0 0' : '10px',
              padding: shipIsMobile ? 12 : 16,
              width: shipIsMobile ? '100%' : '95%',
              maxWidth: shipIsMobile ? '100%' : '720px',
              maxHeight: shipIsMobile ? '92vh' : '80vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 0 20px rgba(0,0,0,0.4)',
              boxSizing: 'border-box',
              paddingBottom: shipIsMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                gap: 8,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: shipIsMobile ? '1rem' : '1.1rem' }}>Ship a Design</h3>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Owner: <span style={{ fontWeight: 800 }}>{adminUsername || '(unknown)'}</span>
                </div>
              </div>

              <button
                onClick={closeShipOverlay}
                disabled={isVerifyingAdmin}
                style={{
                  ...buttonStyle('#6b7280'),
                  padding: shipIsMobile ? '6px 10px' : '4px 10px',
                  fontSize: shipIsMobile ? '0.85rem' : '0.8rem',
                  touchAction: 'manipulation',
                }}
              >
                Close
              </button>
            </div>

            {shipLoading && <div style={{ padding: '8px 0' }}>Loading…</div>}
            {shipError && <div style={{ color: 'red', marginBottom: 10 }}>{shipError}</div>}

            {!shipLoading && !shipError && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 800 }}>Project:</div>
                  <select
                    value={shipSelectedProject}
                    onChange={(e) => setShipSelectedProject(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  >
                    {shipProjects.length === 0 ? (
                      <option value="">No projects found</option>
                    ) : (
                      shipProjects.map((p) => (
                        <option key={p.projectSlug} value={p.projectSlug}>
                          {p.label || p.projectSlug}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 800 }}>User:</div>
                  <select
                    value={shipSelectedUserEmail}
                    onChange={(e) => setShipSelectedUserEmail(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  >
                    {shipUsers.length === 0 ? (
                      <option value="">No users found</option>
                    ) : (
                      shipUsers.map((u) => (
                        <option key={u.email} value={u.email}>
                          {u.email} ({u.tier})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 800 }}>Delivery Type:</div>

                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: 260,
                      height: 38,
                      borderRadius: 999,
                      border: '1px solid #d1d5db',
                      backgroundColor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 8px',
                      boxSizing: 'border-box',
                      userSelect: 'none',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 3,
                        bottom: 3,
                        width: 'calc(50% - 6px)',
                        left: shipDeliveryMode === 'free' ? 3 : 'calc(50% + 1px)',
                        borderRadius: 999,
                        backgroundColor: shipDeliveryMode === 'free' ? '#22c55e' : '#3b82f6',
                        transition: 'left 0.2s ease, background-color 0.2s ease',
                        zIndex: 0,
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => setShipDeliveryMode('free')}
                      style={{
                        flex: 1,
                        height: '100%',
                        background: 'transparent',
                        border: 'none',
                        fontWeight: shipDeliveryMode === 'free' ? 800 : 600,
                        color: '#111827',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    >
                      Free
                    </button>

                    <button
                      type="button"
                      onClick={() => setShipDeliveryMode('credits')}
                      style={{
                        flex: 1,
                        height: '100%',
                        background: 'transparent',
                        border: 'none',
                        fontWeight: shipDeliveryMode === 'credits' ? 800 : 600,
                        color: '#111827',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    >
                      Credits
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 800 }}>Notes (optional)</div>
                  <textarea
                    value={shipNotes}
                    onChange={(e) => setShipNotes(e.target.value)}
                    placeholder="(optional) notes for customer_order.json"
                    rows={shipIsMobile ? 3 : 4}
                    style={{
                      width: '100%',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      padding: 10,
                      fontSize: 13,
                      boxSizing: 'border-box',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleShipToUser}
                    disabled={shipLoading || !shipSelectedProject || !shipSelectedUserEmail || isVerifyingAdmin}
                    style={{
                      ...buttonStyle('#111827'),
                      opacity:
                        shipLoading || !shipSelectedProject || !shipSelectedUserEmail || isVerifyingAdmin ? 0.6 : 1,
                      cursor:
                        shipLoading || !shipSelectedProject || !shipSelectedUserEmail || isVerifyingAdmin
                          ? 'default'
                          : 'pointer',
                    }}
                    title={`Ships this design as "${shipDeliveryMode}" to the selected user`}
                  >
                    Ship to User
                  </button>

                  <button
                    onClick={refreshShipOverlay}
                    disabled={shipLoading || isVerifyingAdmin}
                    style={{
                      ...buttonStyle('#0ea5e9'),
                      opacity: shipLoading || isVerifyingAdmin ? 0.6 : 1,
                      cursor: shipLoading || isVerifyingAdmin ? 'default' : 'pointer',
                    }}
                    title="Reload projects/users"
                  >
                    Refresh
                  </button>
                </div>

                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.35 }}>
                  <ul style={{ margin: '6px 0 0 18px', padding: 0 }}></ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  minWidth: '120px',
  boxSizing: 'border-box',
};

function buttonStyle(bg) {
  return {
    padding: '8px 14px',
    backgroundColor: bg,
    color: 'white',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

const thStyle = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid #e5e7eb',
  backgroundColor: '#f3f4f6',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const tdStyle = {
  padding: '6px 8px',
  borderBottom: '1px solid #e5e7eb',
};